import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { filter, pairwise } from 'rxjs';
import { Router, RoutesRecognized, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { msalConfig } from './auth/auth-config';
import { AuthenticationService } from './auth/authentication.service';
import { RecruitmentSignalRService } from '@v2/core/services/recruitment-signalr.service';
import { ScreeningSignalRService } from '@v2/core/services/screening-signalr.service';
import { PrivacyStatementHttpService } from '@v2/data/service/privacy-statement.http.service';
import { EventService } from '@v2/core/services/event.service';
import { InactivityService } from '@v2/core/services/inactivity.service';
import { LoadingIndicatorComponent } from './layout/loader/loading-indicator.component';
import { LoadingService } from './layout/loader/loading.service';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterOutlet, LoadingIndicatorComponent],
})
export class AppComponent implements OnInit {
  title = 'Magnit Portal';
  isRouterStateReady = signal(false);

  private destroyRef = inject(DestroyRef);
  private authenticationService = inject(AuthenticationService);

  constructor(
    private recruitmentSignalRService: RecruitmentSignalRService,
    private screeningSignalRService: ScreeningSignalRService,
    private privacyStatementService: PrivacyStatementHttpService, // <-- without this, translation of `app.componet.ts` is broken ¯\_(ツ)_/¯
    private router: Router,
    private readonly eventService: EventService,
    private inactivityService: InactivityService,
    private readonly loadingService: LoadingService,
  ) {
    this.configureRouterEvents();
    this.watchForIdleActivity();
  }

  ngOnInit(): void {
    this.loadingService.loadingOn();

    this.authenticationService
      .handleRedirectAndEvents()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((authResult) => {
        if (authResult) {
          const isSupplierAccount = msalConfig.clientId === authResult.account.idTokenClaims?.aud;
          if (isSupplierAccount) {
            this.router.navigate(['/supplier/home']).then(() => {
              this.loadingService.loadingOff();
              this.isRouterStateReady.set(true);
            });
          }
        } else {
          this.isRouterStateReady.set(true);
          this.loadingService.loadingOff();
        }
      });

    this.recruitmentSignalRService.init();
    this.screeningSignalRService.init();
  }

  private configureRouterEvents(): void {
    this.router.events
      .pipe(
        filter((event: any) => event instanceof RoutesRecognized),
        pairwise(),
      )
      .subscribe((events: RoutesRecognized[]) => {
        this.eventService.previousNavigatedRoute.next(events[0].urlAfterRedirects);
      });
  }

  private watchForIdleActivity(): void {
    this.inactivityService.onInactive$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.authenticationService.logout();
    });
  }
}
